import React from "react";
import { useNavigate } from "react-router-dom";
import { 
  Server, 
  Monitor, 
  Rocket, 
  Cloud, 
  Shield, 
  Zap,
  ArrowRight
} from "lucide-react";

const HomePage = () => {
  const navigate = useNavigate();

  const features = [
    {
      icon: <Server className="w-8 h-8 text-indigo-600" />,
      title: "Server-Side Apps",
      description: "Deploy and manage your server applications with automated scaling and monitoring.",
      link: "/Server-Side-Apps",
      stats: [
        { label: "Auto Scaling", value: "✓" },
        { label: "Load Balancing", value: "✓" },
        { label: "Health Checks", value: "✓" }
      ]
    },
    {
      icon: <Monitor className="w-8 h-8 text-indigo-600" />,
      title: "Client-Side Apps",
      description: "Host and deploy your static websites and client applications with global CDN support.",
      link: "/Client-Side-Apps",
      stats: [
        { label: "Global CDN", value: "✓" },
        { label: "SSL Included", value: "✓" },
        { label: "Custom Domain", value: "✓" }
      ]
    }
  ];

  const benefits = [
    {
      icon: <Rocket className="w-5 h-5" />,
      title: "Quick Deployment",
      description: "Deploy your applications in minutes with our streamlined process."
    },
    {
      icon: <Cloud className="w-5 h-5" />,
      title: "Cloud Native",
      description: "Built on modern cloud infrastructure for reliability and scalability."
    },
    {
      icon: <Shield className="w-5 h-5" />,
      title: "Security First",
      description: "Enterprise-grade security with automated updates and monitoring."
    },
    {
      icon: <Zap className="w-5 h-5" />,
      title: "High Performance",
      description: "Optimized infrastructure for maximum performance and reliability."
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white">
      {/* Hero Section */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-16 pb-24">
        <div className="text-center">
          <h1 className="text-4xl font-bold text-gray-900 sm:text-5xl md:text-6xl font-display">
            Welcome to <span className="text-indigo-600">Cloudify</span>
          </h1>
          <p className="mt-3 max-w-md mx-auto text-base text-gray-500 sm:text-lg md:mt-5 md:text-xl md:max-w-3xl">
            Streamline your development workflow with our self-service platform. Deploy, manage, and scale your applications with ease.
          </p>
        </div>

        {/* Main Features */}
        <div className="mt-16 grid grid-cols-1 md:grid-cols-2 gap-8">
          {features.map((feature, index) => (
            <div
              key={index}
              onClick={() => navigate(feature.link)}
              className="group relative bg-white p-8 rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 cursor-pointer border border-gray-100 overflow-hidden"
            >
              <div className="absolute top-0 right-0 w-32 h-32 transform translate-x-16 -translate-y-16">
                <div className="absolute inset-0 bg-indigo-50 rounded-full opacity-20"></div>
              </div>
              
              <div className="relative">
                <div className="flex items-center space-x-4 mb-6">
                  <div className="flex items-center justify-center w-16 h-16 bg-indigo-50 rounded-xl group-hover:scale-110 transition-transform duration-300">
                    {feature.icon}
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900 group-hover:text-indigo-600 transition-colors">
                    {feature.title}
                  </h3>
                </div>

                <p className="text-gray-600 text-lg mb-8 leading-relaxed">
                  {feature.description}
                </p>

                <div className="flex items-center justify-between">
                  <div className="flex items-center text-indigo-600 font-semibold group">
                    Get Started
                    <ArrowRight className="w-5 h-5 ml-2 transform group-hover:translate-x-1 transition-transform" />
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Benefits Section */}
        <div className="mt-24">
          <h2 className="text-3xl font-bold text-gray-900 text-center mb-12">
            Why Choose Cloudify?
          </h2>
          <div className="grid gap-8 md:grid-cols-4">
            {benefits.map((benefit, index) => (
              <div key={index} className="text-center">
                <div className="flex items-center justify-center w-10 h-10 mx-auto bg-indigo-50 rounded-full mb-4">
                  {benefit.icon}
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">
                  {benefit.title}
                </h3>
                <p className="text-gray-500 text-sm">
                  {benefit.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default HomePage;